// eslint-disable-next-line
// @ts-nocheck
import '../../components/nav/navEditor';
import { CuiLayout } from 'cui';
import { bind, wire } from 'hyperhtml';
import getResource from '../../core/resources';
import odataFetch from '../../core/odataFetch';
import applyNavChanges from './navDataConverter';
import tocEditorCuiActions from './tocEditorCuiLayoutActions';
import SvgManager from '../../core/SvgManager';
import xml from '../../core/Xml';
import { getFormChange, getFormData } from './formDataConverter';
import renderViewAs from './viewAs';
import saveTOC from './saveTOC';
import notify from '../../components/notify';
import '../../components/formView';

const notifyTOCSaving = (): void => {
	const successMessage = getResource('toc_editor.toc_saved');
	notify(successMessage, { type: 'success' });
	window.mainLayout.observer.notify('UpdateTOC');
};

export default class TocEditorCuiLayout extends CuiLayout {
	actions = tocEditorCuiActions(this.setState.bind(this));
	initialState = {
		changes: {},
		viewAs: null,
		defaultLanguageCode: 'en',
		languages: [{ code: 'en', name: 'English' }],
		selectedSortingType: '',
		sortingType: '',
		sortingTypesList: [],
		selectedId: '',
		nextToSelectedId: '',
		prevToSelectedId: '',
		tocData: new Map()
	};

	async init() {
		const contentsTitle = getResource('common.contents');
		const header = wire()`
			<div class="toc-editor__header" />
		`;
		const nav = wire()`
			<aras-nav-editor class="toc-editor__nav" drag-on />
		`;
		nav.on('click', (key) => this.actions.selectItem(key));
		nav.on('dropitem', (_, event) => this.actions.dropItem(event.detail));
		const formHeader = wire()`
			<div class="toc-editor__form-header" />
		`;
		const formView = wire()`
			<aras-form-view class="toc-editor__form" />
		`;
		const spinner = wire()`
			<div class="aras-spinner-container aras-hide" id="toc_editor_spinner">
				<div class="aras-centered">
					<div class="aras-spinner"></div>
				</div>
			</div>
		`;
		const viewAsForm = this._renderViewAsForm(this);

		const onTitleClick = () => this.actions.selectItem('');
		bind(this.dom)`
			<div class="toc-editor">
				${header}
				<div class="toc-editor__content">
					<div class="toc-editor__pane toc-editor__nav-container">
						<div class="toc-editor__nav-title-container">
							<span role="button" class="toc-editor__nav-title" onclick="${onTitleClick}">
								${contentsTitle}
							</span>
						</div>
						${nav}
						${viewAsForm}
					</div>
					<div class="toc-editor__pane">
						${formHeader}
						${formView}
					</div>
				</div>
				${spinner}
			</div>
		`;

		this.dom = header;
		this.nav = nav;
		this.formHeader = formHeader;
		this.formView = formView;
		this.formView.addEventListener('change', (e) => {
			const detail = e.detail;
			if (!detail) {
				return;
			}

			const state = this.state;
			const change = getFormChange(this.formView, state, detail);
			if (!change) {
				return;
			}

			const { changes, selectedId, tocData } = state;
			if (!selectedId) {
				const { sortingType } = change;
				this.actions.setSelectedSortingType(sortingType);
				return;
			}

			const relshipProperties = [
				'role@aras.id',
				'role@aras.keyed_name',
				'sort_order'
			];
			const changedProperties = Object.keys(change);
			const relshipChanged = relshipProperties.some((property) =>
				changedProperties.includes(property)
			);
			const relshipItem = {
				...tocData.get(selectedId),
				...changes[selectedId]
			};
			const id = relshipChanged ? relshipItem.id : relshipItem.related_id;
			this.actions.editItem(id, change);
		});

		aras.browserHelper.toggleSpinner(document, true, 'toc_editor_spinner');

		try {
			await Promise.all([
				this._initTOC(nav),
				this._getSortPreferences(),
				super.init()
			]);

			this._initLanguageData();
			this._registerTocEditorHandlers();
		} finally {
			aras.browserHelper.toggleSpinner(document, false, 'toc_editor_spinner');
		}
	}

	updateLayout(oldProps, oldState) {
		const {
			changes: oldChanges,
			selectedId: oldSelectedId,
			tocData: oldTocData,
			viewAs: oldViewAs
		} = oldState;
		const { changes, selectedId, tocData, viewAs } = this.state;
		const data = this._applyDataChanges();
		const hasNewChanegs = oldChanges !== changes || oldTocData !== tocData;
		if (hasNewChanegs) {
			applyNavChanges(this.nav, data);
		}

		const newItemSelected = oldSelectedId !== selectedId;
		if (newItemSelected) {
			this._selectItem(data);
		}

		const viewAsChanged = oldViewAs !== viewAs;
		if (hasNewChanegs || newItemSelected || viewAsChanged) {
			this._renderForm(data);
		}

		if (viewAsChanged) {
			this.nav.dragOn = !viewAs;
		}

		this._renderViewAsForm(this);
	}

	async saveTOC(): Promise<boolean> {
		const tocData = this._applyDataChanges();
		const tocEditorSaved = await saveTOC(this.state, tocData);
		if (tocEditorSaved) {
			notifyTOCSaving();
		}

		return tocEditorSaved;
	}

	_selectItem(data) {
		const { changes, selectedId } = this.state;

		this.nav.select(selectedId);

		const selectedItem = data.get(selectedId);
		if (!selectedItem) {
			return;
		}

		const isNewItem = selectedItem['@aras.action'] === 'add';
		if (!isNewItem) {
			return;
		}

		const selectedRelatedId = selectedItem.related_id;
		const selectedRelatedItem = changes[selectedRelatedId];
		const selectedItemParentMenu = selectedRelatedItem['parent_menu@aras.id'];
		const itemToExpand = [...data.values()].find(
			(el) => el.related_id === selectedItemParentMenu
		);

		if (itemToExpand) {
			this.nav.expand(itemToExpand.id, true);
		}
	}

	_applyDataChanges() {
		const { changes, tocData } = this.state;
		const changeEntries = Object.entries(changes);
		if (changeEntries.length === 0) {
			return tocData;
		}

		const newData = new Map([...tocData]);
		for (const [id, change] of changeEntries) {
			const oldItem = tocData.get(id);
			if (!oldItem) {
				newData.set(id, change);
				continue;
			}

			newData.set(id, { ...oldItem, ...change });
		}

		return newData;
	}

	_buildDataForToc(rawData) {
		return rawData.reduce((acc, relship) => {
			const { id, related_id: relatedItem } = relship;

			delete relatedItem['label@aras.lang'];
			if (relatedItem['@odata.type'] === '#CommandBarButton') {
				delete relatedItem['parent_menu'];
			}

			acc.set(relatedItem.id, {
				...relatedItem,
				additional_data: JSON.parse(relatedItem.additional_data)
			});
			acc.set(id, {
				...relship,
				'@odata.type': '#CommandBarSectionItem',
				related_id: relatedItem.id
			});
			return acc;
		}, new Map());
	}

	async _getSortingTypesList() {
		const list = await aras.MetadataCacheJson.GetList([
			'8D88E35D2B3C4E98AD954767736F724F'
		]);
		const sortingTypeTable = list?.[0]?.value?.[0];
		const sortingTypeList = sortingTypeTable?.Value.map(({ label, value }) => ({
			label,
			value
		}));
		return sortingTypeList;
	}

	async _getCurrentSortingType() {
		const worldPreferenceId = '507A16AA36F94513AD7EC1F948B5B48F';
		const currentSortingTypeUrl = `Preference('${worldPreferenceId}')/Core_GlobalLayout('DB4652A5C55D4EB38F39E5C0602CEC48')/core_toc_sorting_type`;
		const { value: currentSortingType } = await odataFetch(
			currentSortingTypeUrl
		);
		return currentSortingType;
	}

	async _getSortPreferences() {
		const currentSortingTypePromise = this._getCurrentSortingType();
		const sortingTypesListPromise = this._getSortingTypesList();
		const promises = [currentSortingTypePromise, sortingTypesListPromise];
		const [sortingType = '', sortingTypesList = []] = await Promise.all(
			promises
		);
		const { setSelectedSortingType, setSortingType, setSortingTypesList } =
			this.actions;
		setSelectedSortingType(sortingType);
		setSortingType(sortingType);
		setSortingTypesList(sortingTypesList);
	}

	async _fetchTOCData() {
		const odataFilter = `action eq 'Add' and source_id/location/name eq 'TOC'`;
		const odataRelatedSelect = `id,image,name,label,parent_menu,additional_data,command_alias,on_click_handler`;
		const requestURL = `
			CommandBarSectionItem?
			$select=id,role,sort_order,source_id&
			$orderby=sort_order&
			$filter=${odataFilter}&
			$expand=related_id($select=${odataRelatedSelect};lang=*)
		`;
		const data = await odataFetch(requestURL);

		return this._buildDataForToc(data.value);
	}

	async _fetchTOCDataForIdentity() {
		const requestURL = `method.cui_GetTocForIdentityInODATAForm`;
		const headers = {};
		const { viewAs } = this.state;
		if (viewAs.language) {
			const selectedLanguage = viewAs.prevLanguages.find(
				(lang) => lang.name === viewAs.language.label
			);
			headers['Accept-Language'] = selectedLanguage.code;
		}
		const data = await odataFetch(requestURL, {
			method: 'POST',
			headers,
			body: JSON.stringify({
				identity: viewAs.identity.value
			})
		});

		return this._buildDataForToc(data.value);
	}

	async _updateTocDataForIdentity() {
		const tocData = await this._fetchTOCDataForIdentity();
		this.setState((state) => {
			state.tocData = tocData;
		});
	}

	async _initTOC() {
		const tocData = await this._fetchTOCData();
		this.setState((state) => {
			state.changes = {};
			state.tocData = tocData;
		});
		if (!tocData.has(this.state.selectedId)) {
			this.actions.selectItem('');
		}
	}
	_renderForm(stateData) {
		const formData = getFormData(stateData, this.state);
		if (!formData) {
			bind(this.formHeader)``;
			this.formView.order = [];
			this.formView.render();
			return;
		}

		this.formView.resetState();
		const { data, image, label, metadata, order } = formData;
		bind(this.formHeader)`
			${SvgManager.createHyperHTMLNode(image, {
				cssClass: 'toc-editor__form-header-image'
			})}
			<span class="toc-editor__form-header-label">${label}</span>
		`;

		this.formView.metadata = metadata;
		this.formView.data = data;
		this.formView.order = order;
		this.formView.render();
	}

	_renderViewAsForm() {
		const formChangeHandler = async () => {
			aras.browserHelper.toggleSpinner(document, true, 'toc_editor_spinner');

			try {
				await this._updateTocDataForIdentity();
			} finally {
				aras.browserHelper.toggleSpinner(document, false, 'toc_editor_spinner');
			}
		};

		return renderViewAs(this, formChangeHandler);
	}

	_initLanguageData() {
		this.setState((state) => {
			state.defaultLanguageCode = aras.getSessionContextLanguageCode();
			state.languages = xml
				.selectNodes(aras.getLanguagesResultNd(), 'Item[@type="Language"]')
				.map((language) => {
					const codePropertyNode = xml.selectSingleNode(language, 'code');
					const code = codePropertyNode?.text;
					const namePropertyNode = xml.selectSingleNode(language, 'name');
					const name = namePropertyNode?.text;
					return { code, name };
				});
		});
	}

	_registerTocEditorHandlers() {
		this.observer.subscribe(async (event) => {
			if (event !== 'reinitTOCEditor') {
				return;
			}

			const initTocPromise = this._initTOC(this.nav);
			const preferencesPromise = this._getSortPreferences();
			await Promise.all([initTocPromise, preferencesPromise]);
		});
	}
}
